import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { PageIndicator, ThemeProvider } from "@sunday/component";

export interface CarouselBannerProps {
  images?: string[];
  mobileImages?: string[];
  autoPlayInterval?: number;
}

interface SlideProps {
  active: boolean;
  bgImage?: string;
  mobileBgImage?: string;
}

const ArrowLeftIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M38.5 24H9.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M24 38.5L9.5 24L24 9.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.5 24H38.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M24 9.5L38.5 24L24 38.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  gap: 16px;
  isolation: isolate;
  position: relative;
  width: 100%;
  max-width: 1388px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
    gap: 12px;
    align-items: center;
    width: 100%;
  }
`;

const BannerArea = styled.div`
  position: relative;
  width: 100%;
  max-width: 1388px;
  height: 360px;

  @media (max-width: 1024px) {
    height: 300px;
  }

  @media (max-width: 768px) {
    height: 145px;
    overflow: visible;
  }
`;

const SlideContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-sizing: border-box;
  background: #f1f3f5;
  z-index: 0;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0 16px;
    margin: 0 -16px;
    gap: 6px;
    width: calc(100% + 16px);
    height: 145px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    background: transparent;
    border-radius: 0;

    /* Hide scrollbars */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const Slide = styled.div<SlideProps>`
  position: absolute;
  inset: 0;
  opacity: ${(props) => (props.active ? 1 : 0)};
  visibility: ${(props) => (props.active ? "visible" : "hidden")};
  transition: opacity 0.5s ease-in-out, visibility 0.5s ease-in-out;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${(props) =>
    props.bgImage ? `url(${props.bgImage})` : "none"};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;

  ${(props) =>
    !props.bgImage &&
    `
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%);
    box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.02);
  `}

  @media (max-width: 768px) {
    position: relative;
    inset: auto;
    width: calc(100vw - 32px);
    max-width: 100%;
    height: 145px;
    flex: 0 0 calc(100vw - 32px);
    opacity: 1 !important;
    visibility: visible !important;
    border-radius: 12px;
    scroll-snap-align: center;
    background-size: cover;
    background-position: center;
    background-image: ${(props) => {
      const responsiveImage = props.mobileBgImage || props.bgImage;
      return responsiveImage ? `url(${responsiveImage})` : "none";
    }};
  }
`;

const SlidePlaceholderText = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #a0a4a8;
  user-select: none;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ArrowButton = styled.button`
  box-sizing: border-box;
  position: absolute;
  width: 80px;
  height: 80px;
  top: calc(50% - 40px); /* Center vertically relative to banner height */
  background: #ffffff;
  border: 1px solid #d9dbde;
  box-shadow: 0px 4px 18px 5px rgba(43, 43, 43, 0.05);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;
  padding: 0;
  outline: none;

  &:hover {
    background-color: #fafafa;
    transform: scale(1.04);
    box-shadow: 0px 6px 20px 6px rgba(43, 43, 43, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const LeftArrow = styled(ArrowButton)`
  left: -40px;
  z-index: 3;
`;

const RightArrow = styled(ArrowButton)`
  right: -40px;
  z-index: 2;
`;

const DesktopIndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileIndicatorWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 4px;
    width: 343px;
    max-width: 100%;
    margin-top: 8px;
  }
`;

const IndicatorDot = styled.button<{ $active: boolean }>`
  width: ${(props) => (props.$active ? "16px" : "4px")};
  height: 4px;
  border-radius: 5px;
  background-color: ${(props) => (props.$active ? "#435DFF" : "#9FA6AD")};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: width 0.3s ease, background-color 0.3s ease;
  flex: none;
  outline: none;
`;

export const CarouselBanner = ({
  images = [],
  mobileImages = [],
  autoPlayInterval = 5000,
}: CarouselBannerProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const isManualScroll = useRef(false);

  const hasImages = images && images.length > 0;

  const slides = hasImages
    ? images.map((imgUrl, idx) => ({
        id: `slide-img-${idx}-${imgUrl.substring(0, 10)}`,
        bgImage: imgUrl,
        mobileBgImage: mobileImages[idx],
        isPlaceholder: false,
        label: `Slide ${idx + 1}`,
      }))
    : Array.from({ length: 3 }).map((_, idx) => ({
        id: `slide-placeholder-${idx}`,
        bgImage: undefined,
        mobileBgImage: undefined,
        isPlaceholder: true,
        label: `ICTH Banner Placeholder ${idx + 1}`,
      }));

  const slideCount = slides.length;

  const handleNext = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % slideCount);
  }, [slideCount]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + slideCount) % slideCount);
  }, [slideCount]);

  const handleScroll = () => {
    if (!slideContainerRef.current || isManualScroll.current) return;
    const container = slideContainerRef.current;
    const scrollPosition = container.scrollLeft;
    const slideWidth =
      container.firstElementChild?.clientWidth || container.clientWidth;
    const slideStep = slideWidth + 6;
    const newIndex = Math.round(scrollPosition / slideStep);
    if (newIndex >= 0 && newIndex < slideCount && newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  };

  const handlePageIndicatorChange = (index: number) => {
    setActiveIndex(index);
    if (slideContainerRef.current && window.innerWidth <= 768) {
      isManualScroll.current = true;
      const container = slideContainerRef.current;
      const slideWidth =
        container.firstElementChild?.clientWidth || container.clientWidth;
      const slideStep = slideWidth + 6;
      container.scrollTo({
        left: index * slideStep,
        behavior: "smooth",
      });
      setTimeout(() => {
        isManualScroll.current = false;
      }, 500);
    }
  };

  useEffect(() => {
    if (autoPlayInterval > 0 && !isHovered) {
      autoPlayTimerRef.current = setInterval(() => {
        handleNext();
      }, autoPlayInterval);
    }

    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
      }
    };
  }, [isHovered, autoPlayInterval, handleNext]);

  useEffect(() => {
    if (
      slideContainerRef.current &&
      window.innerWidth <= 768 &&
      !isManualScroll.current
    ) {
      const container = slideContainerRef.current;
      const slideWidth =
        container.firstElementChild?.clientWidth || container.clientWidth;
      const slideStep = slideWidth + 6;
      container.scrollTo({
        left: activeIndex * slideStep,
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  return (
    <Container
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <BannerArea>
        {/* Navigation Arrows */}
        <LeftArrow onClick={handlePrev} aria-label="Previous slide">
          <ArrowLeftIcon />
        </LeftArrow>

        <RightArrow onClick={handleNext} aria-label="Next slide">
          <ArrowRightIcon />
        </RightArrow>

        {/* Slides Area */}
        <SlideContainer ref={slideContainerRef} onScroll={handleScroll}>
          {slides.map((slide, index) => (
            <Slide
              key={slide.id}
              active={index === activeIndex}
              bgImage={slide.bgImage}
              mobileBgImage={slide.mobileBgImage}
            >
              {slide.isPlaceholder && (
                <SlidePlaceholderText>{slide.label}</SlidePlaceholderText>
              )}
            </Slide>
          ))}
        </SlideContainer>
      </BannerArea>
      <DesktopIndicatorWrapper>
        <ThemeProvider theme="ice-cream">
          <PageIndicator
            total={slideCount}
            current={activeIndex}
            onChange={handlePageIndicatorChange}
          />
        </ThemeProvider>
      </DesktopIndicatorWrapper>
      <MobileIndicatorWrapper>
        {slides.map((slide, idx) => (
          <IndicatorDot
            key={slide.id}
            $active={idx === activeIndex}
            onClick={() => handlePageIndicatorChange(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </MobileIndicatorWrapper>
    </Container>
  );
};

export default CarouselBanner;
